import React from "react";
import clsx from "clsx";
import moment from "moment";
import {
  Box,
  Divider,
  Grow,
  InputBase,
  List,
  ListItem,
  ListItemText,
  Paper,
  Popper,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  LocalizationProvider,
  makeRangePicker,
} from "@material-ui/pickers-v4";
import { W as WrapperVariantContext } from "@material-ui/pickers-v4/Clock-c2c3c22b.js";
import AdapterMoment from "@material-ui/pickers-v4/adapter/moment";

const useStyles = makeStyles((theme) => ({
  root: {
  },
  popper: {
    zIndex: theme.zIndex.modal,
  },
  popperPaper: {
    display: "flex",
    alignItems: "stretch",
    overflow: "hidden",
  },
  presetsColumn: {
    minWidth: 180,
    padding: theme.spacing(1, 1.5),
  },
  presetsTitle: {
    fontWeight: 600,
    textTransform: "uppercase",
    fontSize: 12,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
  presetsList: {
    padding: 0,
  },
  presetItem: {
    borderRadius: 6,
    "&.Mui-selected, &.Mui-selected:hover": {
      backgroundColor: theme.palette.action.selected,
    },
  },
  calendarColumn: {
    display: "flex",
    alignItems: "stretch",
  },
  rangeRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
  },
  rangeLabel: {
    // keep label style consistent with existing date inputs
  },
  rangeInput: {
    padding: "2px 8px",
  },
  rangeInputOutlined: {
    border: `1px solid rgba(0, 0, 0, 0.23)`,
    borderRadius: 4,
    padding: "4px 10px",
    '&:hover': {
      border: `1px solid rgba(0, 0, 0, 0.87)`,
    },
  },
  rangeInputInner: {
    ...theme.typography.button,
    fontWeight: 700,
    textTransform: "uppercase",
    padding: 0,
    width: "11ch",
    height: 24,
    lineHeight: "auto",
    fontVariantNumeric: "tabular-nums",
    colorScheme: theme.palette.type,
    appearance: "none",
    WebkitAppearance: "none",
    "&::-webkit-calendar-picker-indicator": {
      display: "none",
    },
    "&::-webkit-clear-button": {
      display: "none",
    },
    "&::-webkit-inner-spin-button": {
      display: "none",
    },
  },
}));

const PresetDateRangeWrapper = (props) => {
  const {
    children,
    DateInputProps,
    KeyboardDateInputComponent,
    onDismiss,
    open,
    PopperProps,
    TransitionComponent = Grow,
    presets = [],
    presetsTitle,
  } = props;
  const classes = useStyles();
  const inputRef = React.useRef(null);
  const popperRef = React.useRef(null);

  const handleBlur = React.useCallback(() => {
    window.setTimeout(() => {
      const activeEl = document.activeElement;
      if (
        (inputRef.current && inputRef.current.contains(activeEl)) ||
        (popperRef.current && popperRef.current.contains(activeEl))
      ) {
        return;
      }
      onDismiss();
    }, 0);
  }, [onDismiss]);

  const handlePresetClick = React.useCallback(
    (preset) => {
      if (!preset || !preset.getRange || !DateInputProps?.onChange) {
        return;
      }
      const range = preset.getRange();
      DateInputProps.onChange(range);
      onDismiss();
    },
    [DateInputProps, onDismiss]
  );

  const KeyboardInput = KeyboardDateInputComponent;
  const hasPresets = Array.isArray(presets) && presets.length > 0;
  const { className: popperClassNameProp, ...popperBaseProps } = PopperProps || {};
  const popperClassName = clsx(classes.popper, popperClassNameProp);

  return (
    <WrapperVariantContext.Provider value="desktop">
      <React.Fragment>
        <KeyboardInput {...DateInputProps} containerRef={inputRef} onBlur={handleBlur} />
        <Popper
          transition
          role="dialog"
          open={open}
          anchorEl={inputRef.current}
          className={popperClassName}
          {...popperBaseProps}
        >
          {({ TransitionProps }) => (
            <TransitionComponent {...TransitionProps}>
              <Paper ref={popperRef} tabIndex={-1} elevation={8} onBlur={handleBlur}>
                <div className={classes.popperPaper}>
                  {hasPresets && (
                    <div className={classes.presetsColumn}>
                      {presetsTitle ? (
                        <Typography className={classes.presetsTitle}>{presetsTitle}</Typography>
                      ) : null}
                      <List className={classes.presetsList}>
                        {presets.map((preset) => (
                          <ListItem
                            key={preset.key || preset.label}
                            button
                            dense
                            className={classes.presetItem}
                            selected={Boolean(preset.isSelected)}
                            onClick={() => handlePresetClick(preset)}
                          >
                            <ListItemText primary={preset.label} />
                          </ListItem>
                        ))}
                      </List>
                    </div>
                  )}
                  {hasPresets && <Divider orientation="vertical" flexItem />}
                  <div className={classes.calendarColumn}>{children}</div>
                </div>
              </Paper>
            </TransitionComponent>
          )}
        </Popper>
      </React.Fragment>
    </WrapperVariantContext.Provider>
  );
};

const PresetDateRangePicker = makeRangePicker(
  "MuiPickersPresetDateRangePicker",
  PresetDateRangeWrapper
);

const DateRangePickerTest = ({
  value,
  onChange,
  presets,
  presetsTitle,
  startText = "Start",
  endText = "End",
  locale,
  outlined = false,
}) => {
  const classes = useStyles();
  const inputLocale = locale || undefined;
  const isSafari = React.useMemo(() => {
    if (typeof navigator === "undefined") return false;
    const ua = navigator.userAgent;
    return /safari/i.test(ua) && !/chrome|chromium|android/i.test(ua);
  }, []);
  const startMomentValue = value?.[0];
  const endMomentValue = value?.[1];
  const startInputValue =
    startMomentValue && startMomentValue.isValid && startMomentValue.isValid()
      ? startMomentValue.format("YYYY-MM-DD")
      : "";
  const endInputValue =
    endMomentValue && endMomentValue.isValid && endMomentValue.isValid()
      ? endMomentValue.format("YYYY-MM-DD")
      : "";
  const nextMonthPreset = React.useMemo(() => {
    const isFrenchLocale =
      typeof inputLocale === "string" && inputLocale.toLowerCase().startsWith("fr");
    return {
      key: "next_month",
      label: isFrenchLocale ? "Mois prochain" : "Next month",
      getRange: () => {
        const next = moment().add(1, "month");
        return [next.clone().startOf("month"), next.clone().endOf("month")];
      },
    };
  }, [inputLocale]);
  const mergedPresets = React.useMemo(() => {
    if (!Array.isArray(presets) || presets.length === 0) {
      return presets;
    }
    const hasNextMonth = presets.some((preset) => preset?.key === "next_month");
    if (hasNextMonth) {
      return presets;
    }
    return [...presets, nextMonthPreset];
  }, [nextMonthPreset, presets]);
  const normalizedRange = React.useMemo(() => {
    const startValid =
      startMomentValue && startMomentValue.isValid && startMomentValue.isValid();
    const endValid = endMomentValue && endMomentValue.isValid && endMomentValue.isValid();
    if (!startValid || !endValid) {
      return [null, null];
    }
    if (startMomentValue.isAfter(endMomentValue)) {
      return [endMomentValue, startMomentValue];
    }
    return [startMomentValue, endMomentValue];
  }, [endMomentValue, startMomentValue]);
  const presetsWithSelection = React.useMemo(() => {
    if (!Array.isArray(mergedPresets) || mergedPresets.length === 0) {
      return mergedPresets;
    }
    const [rangeStart, rangeEnd] = normalizedRange;
    const rangeStartValid = rangeStart && rangeStart.isValid && rangeStart.isValid();
    const rangeEndValid = rangeEnd && rangeEnd.isValid && rangeEnd.isValid();
    return mergedPresets.map((preset) => {
      if (!preset?.getRange || !rangeStartValid || !rangeEndValid) {
        return { ...preset, isSelected: false };
      }
      const [presetStart, presetEnd] = preset.getRange() || [];
      const presetStartValid =
        presetStart && presetStart.isValid && presetStart.isValid();
      const presetEndValid = presetEnd && presetEnd.isValid && presetEnd.isValid();
      const isSelected =
        presetStartValid &&
        presetEndValid &&
        presetStart.isSame(rangeStart, "day") &&
        presetEnd.isSame(rangeEnd, "day");
      return { ...preset, isSelected };
    });
  }, [mergedPresets, normalizedRange]);
  const handleInputClick = React.useCallback(
    (event) => {
      if (!isSafari) return;
      event.preventDefault();
      event.stopPropagation();
    },
    [isSafari]
  );
  const handleInputFocus = React.useCallback(
    (event) => {
      if (!isSafari) return;
      event.preventDefault();
      event.stopPropagation();
    },
    [isSafari]
  );
  const handleRangeChange = React.useCallback(
    (newRange) => {
      if (!onChange) return;
      const [start, end] = newRange || [];
      const startValid = start && start.isValid && start.isValid();
      const endValid = end && end.isValid && end.isValid();
      if (startValid && endValid && start.isAfter(end)) {
        const [prevStart, prevEnd] = value || [];
        const prevStartValid = prevStart && prevStart.isValid && prevStart.isValid();
        const prevEndValid = prevEnd && prevEnd.isValid && prevEnd.isValid();
        const startChanged = !prevStartValid || !start.isSame(prevStart, "day");
        const endChanged = !prevEndValid || !end.isSame(prevEnd, "day");
        if (startChanged && !endChanged) {
          onChange([start, start]);
          return;
        }
        if (endChanged && !startChanged) {
          onChange([end, end]);
          return;
        }
        onChange(start.isBefore(end) ? [start, end] : [end, start]);
        return;
      }
      onChange(newRange);
    },
    [onChange, value]
  );

  return (
    <LocalizationProvider dateAdapter={AdapterMoment} locale={locale}>
      <Box className={classes.root}>
        <PresetDateRangePicker
          value={value}
          onChange={handleRangeChange}
          startText={startText}
          endText={endText}
          presets={presetsWithSelection}
          presetsTitle={presetsTitle}
          inputFormat="YYYY-MM-DD"
          PopperProps={{ disablePortal: true }}
          renderInput={(startProps, endProps) => (
            <div className={classes.rangeRow}>
              <Typography className={classes.rangeLabel}>{startText}</Typography>
              {(() => {
                const {
                  onChange: startOnChange,
                  className: startInputClassName,
                  ...startInputProps
                } = startProps.inputProps || {};
                delete startInputProps.value;
                const handleStartChange = (event) => {
                  const value = event?.target?.value;
                  if (!value) return;
                  const newStart = moment(value, "YYYY-MM-DD", true);
                  if (!newStart.isValid()) {
                    if (startOnChange) startOnChange(event);
                    return;
                  }
                  const endMoment = endMomentValue;
                  const endValid = endMoment && endMoment.isValid && endMoment.isValid();
                  if (endValid && newStart.isAfter(endMoment)) {
                    handleRangeChange([newStart, newStart]);
                    return;
                  }
                  handleRangeChange([newStart, endValid ? endMoment : null]);
                };
                return (
                  <InputBase
                    type="date"
                    value={startInputValue}
                    onChange={handleStartChange}
                    onInput={handleStartChange}
                    onClick={handleInputClick}
                    onFocus={handleInputFocus}
                    inputRef={startProps.inputRef}
                    className={clsx(classes.rangeInput, outlined && classes.rangeInputOutlined)}
                    inputProps={{
                      ...startInputProps,
                      type: "date",
                      lang: inputLocale,
                      className: clsx(classes.rangeInputInner, startInputClassName),
                    }}
                  />
                );
              })()}
              <Typography className={classes.rangeLabel}>
                {typeof endText === "string" ? endText.toLowerCase() : endText}
              </Typography>
              {(() => {
                const {
                  onChange: endOnChange,
                  className: endInputClassName,
                  ...endInputProps
                } = endProps.inputProps || {};
                delete endInputProps.value;
                const handleEndChange = (event) => {
                  const value = event?.target?.value;
                  if (!value) return;
                  const newEnd = moment(value, "YYYY-MM-DD", true);
                  if (!newEnd.isValid()) {
                    if (endOnChange) endOnChange(event);
                    return;
                  }
                  const startMoment = startMomentValue;
                  const startValid =
                    startMoment && startMoment.isValid && startMoment.isValid();
                  if (startValid && newEnd.isBefore(startMoment)) {
                    handleRangeChange([newEnd, newEnd]);
                    return;
                  }
                  handleRangeChange([startValid ? startMoment : null, newEnd]);
                };
                return (
                  <InputBase
                    type="date"
                    value={endInputValue}
                    onChange={handleEndChange}
                    onInput={handleEndChange}
                    onClick={handleInputClick}
                    onFocus={handleInputFocus}
                    inputRef={endProps.inputRef}
                    className={clsx(classes.rangeInput, outlined && classes.rangeInputOutlined)}
                    inputProps={{
                      ...endInputProps,
                      type: "date",
                      lang: inputLocale,
                      className: clsx(classes.rangeInputInner, endInputClassName),
                    }}
                  />
                );
              })()}
            </div>
          )}
        />
      </Box>
    </LocalizationProvider>
  );
};

export default DateRangePickerTest;
